import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import React, { useState, useMemo } from "react";
import type { ContactDetails, ContactInteraction } from "@/hooks/useContactDetails";
import { format } from "date-fns";

interface InteractionProps {
  contact?: ContactDetails;
  interactions?: ContactInteraction[];
  onAddInteraction?: (title: string, description: string) => Promise<boolean>;
  onUpdateInteraction?: (interactionId: string, title: string, description: string) => Promise<boolean>;
  onDeleteInteraction?: (interactionId: string) => Promise<boolean>;
}

const Interaction: React.FC<InteractionProps> = ({ contact, interactions = [], onAddInteraction }) => {
  const [noteText, setNoteText] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!contact) return { diasAberta: 0, diasNaEtapa: 0, totalInteracoes: 0, diasSemInteracao: 0 };

    const now = new Date();
    const createdDate = new Date(contact.created_at);
    const diasAberta = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    // Get latest opportunity to calculate days in stage
    let diasNaEtapa = 0;
    if (contact.opportunities && contact.opportunities.length > 0) {
      const latestOpp = contact.opportunities[0];
      const oppUpdated = new Date(latestOpp.updated_at);
      diasNaEtapa = Math.floor((now.getTime() - oppUpdated.getTime()) / (1000 * 60 * 60 * 24));
    }

    const totalInteracoes = interactions.length;

    // Days without interaction
    let diasSemInteracao = diasAberta;
    if (contact.last_interaction_at) {
      const lastInteractionDate = new Date(contact.last_interaction_at);
      diasSemInteracao = Math.floor((now.getTime() - lastInteractionDate.getTime()) / (1000 * 60 * 60 * 24));
    }

    return { diasAberta, diasNaEtapa, totalInteracoes, diasSemInteracao };
  }, [contact, interactions]);

  const handleSaveNote = async () => {
    if (!noteText.trim() || !onAddInteraction) return;

    setIsSaving(true);
    try {
      const success = await onAddInteraction("Nota", noteText);
      if (success) {
        setNoteText("");
      }
    } catch (error) {
      console.error("Error saving note:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full text-neutral-50 order-3 xl:order-2 xl:col-span-1 md:col-span-2 col-span-1">
      <div className="rounded-xl flex flex-col gap-4 p-4 border border-[#3F3F464D] bg-[#18181B]">
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center rounded-xl border border-[#3F3F464D] py-3 px-4 bg-[#18181B]">
            <h3 className="text-2xl font-bold text-neutral-50">{metrics.diasAberta}</h3>
            <p className="text-xs font-medium text-neutral-400 mt-1">Dias aberta</p>
          </div>
          <div className="text-center rounded-xl border border-[#3F3F464D] py-3 px-4 bg-[#18181B]">
            <h3 className="text-2xl font-bold text-neutral-50">{metrics.diasNaEtapa}</h3>
            <p className="text-xs font-medium text-neutral-400 mt-1">Dias na etapa</p>
          </div>
          <div className="text-center rounded-xl border border-[#3F3F464D] py-3 px-4 bg-[#18181B]">
            <h3 className="text-2xl font-bold text-neutral-50">{metrics.totalInteracoes}</h3>
            <p className="text-xs font-medium text-neutral-400 mt-1">Interações</p>
          </div>
          <div className="text-center rounded-xl border border-[#3F3F464D] py-3 px-4 bg-[#18181B]">
            <h3 className="text-2xl font-bold text-neutral-50">{metrics.diasSemInteracao}</h3>
            <p className="text-xs font-medium text-neutral-400 mt-1">Dias s/ interação</p>
          </div>
        </div>
        <Textarea
          placeholder="Digite uma anotação"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          className="min-h-[120px] rounded-xl bg-[#18181B] border border-[#3F3F464D] text-neutral-50 placeholder:text-neutral-500"
        />
        <Button
          onClick={handleSaveNote}
          disabled={!noteText.trim() || isSaving}
          className="self-end min-w-[120px] bg-[#E4E4E7] hover:bg-[#D4D4D8]"
        >
          {isSaving ? "Salvando..." : "Salvar"}
        </Button>
      </div>
      <h4 className="mt-8 mb-6 text-xl font-semibold text-neutral-50">Interações</h4>
      <div className="interactions flex flex-col gap-8">
        {interactions.length === 0 ? (
          <div className="text-center text-gray-400 py-8">
            Nenhuma interação registrada
          </div>
        ) : (
          interactions.map((interaction) => (
            <div key={interaction.id} className="flex gap-4 items-start">
              <div className="text-center flex flex-col items-center gap-2 min-w-[60px]">
                <div className="w-8 h-8 rounded-full bg-neutral-700 border-2 border-neutral-600 flex items-center justify-center">
                  <div className="w-3 h-3 rounded-full bg-neutral-400" />
                </div>
                <p className="text-xs text-neutral-400 font-medium">
                  {format(new Date(interaction.created_at), "dd/MM/yyyy")}
                </p>
              </div>
              <div className="px-4 py-3 rounded-xl bg-[#18181B] border border-[#3F3F464D] w-full">
                <p className="text-base font-semibold text-neutral-50">{interaction.title}</p>
                {interaction.description && (
                  <p className="text-sm text-neutral-300 mt-2 whitespace-pre-wrap">
                    {interaction.description}
                  </p>
                )}
              </div>
            </div>
          ))
        )}

        {contact && (
          <div className="flex gap-4 items-start">
            <div className="text-center flex flex-col items-center gap-2 min-w-[60px]">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 border-2 border-blue-500 flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
              </div>
              <p className="text-xs text-neutral-400 font-medium">
                {format(new Date(contact.created_at), "dd/MM/yyyy")}
              </p>
            </div>
            <div className="flex flex-col gap-3 w-full px-4 py-3 rounded-xl bg-[#18181B] border border-[#3F3F464D]">
              <p className="text-base font-semibold text-neutral-50">Contato Criado</p>
              {contact.campaigns && contact.campaigns.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {contact.campaigns.map((campaign, idx) => (
                    <Badge key={idx} className="py-1 px-3">{campaign}</Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Interaction;
